<template>
  <div class="section section-leaderboard">
    <div class="container">
      <div class="section-header text-center">
        <div class="title">
          <h2 class="mb-0">DAILY ISP LEADERBOARD</h2>
        </div>
        <div class="filters">
          <div class="filters-action prev" @click.stop="prevDate()" :class="{'disabled': !isPrevAvailable}">
            <button class="btn btn-outline-light btn-outline-actions">
              <i class="fas fa-chevron-left"></i>
            </button>
          </div>
          <datepicker
            v-model="selectedDate"
            :wrapper-class="'filters-date'"
            :input-class="'date-input'"
            :bootstrap-styling="true"
            :full-month-name="options.fullMonthName"
            :disabled-dates="options.disabledDates" />
          <div class="filters-action next" @click.stop="nextDate()" :class="{'disabled': !isNextAvailable}">
            <button class="btn btn-outline-light btn-outline-actions">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
      <div class="section-filter">
        <ul class="nav nav-tabs nav-fill nav-mode">
          <li class="nav-item"
            @click.stop="changeTab(nav)" v-for="(nav, index) in menuList" :key="index">
            <a class="nav-link" :class="{'active': isActivedTab(nav.key)}">{{nav.name}}</a>
          </li>
        </ul>
        <div class="indicator" :style="{ left: currentOffsetIndicator }"></div>
      </div>
      <div class="section-body">
        <div class="content-container" :style="{ transform: 'translateX' + '(' + currentOffset +')'}">
          <div class="content">
            <LeaderBoard :stats="currentDaily.primeTime2hrs"/>
            <small>เวลา Prime Time หมายถึงช่วงเวลา 20:00 - 22:00 ของแต่ละวัน ซึ่งเป็นช่วงเวลาที่มีการใช้อินเตอร์เน็ตอย่างหนาแน่น และสะท้อนถึงประสบการณ์ใช้งานของผู้ใช้ส่วนใหญ่ที่จะได้รับจากการใช้งานจริง</small>
          </div>
          <div class="content">
            <LeaderBoard :stats="currentDaily.fullDay"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import qs from 'qs';
import moment from 'moment';
import Datepicker from 'vuejs-datepicker';
import { getPercentiles, convertToMbps, formatNumber, convertFormatDate } from '@/modules/utility';
import { loadDailyReport, loadPrimeTimeDailyReport } from '@/modules/api/report';
import LeaderBoard from '@/components/parts/LeaderBoard';

export default {
  name: 'DailyLeaderBoard',
  components: {
    LeaderBoard,
    Datepicker,
  },
  data() {
    return {
      state: {
        showingTab: 'prime-time-period-2hs',
      },
      options: {
        fullMonthName: true,
        disabledDates: {
          from: new Date(),
        },
      },
      menuList: [
        {
          key: 'prime-time-period-2hs',
          name: 'Prime Time',
        },
        {
          key: '24hrs',
          name: '24 Hrs',
        },
      ],
      currentDaily: {
        fullDay: null,
        primeTime2hrs: null,
        primeTime6hrs: null,
      },
      percentile: {
        min: 5,
        max: 95,
      },
      filters: {
        date: null,
      },
      limitOfNextDay: null,
      limitOfISP: 9,
      selectedDate: null,
      sampleSize: 0,
      currentOffset: 0,
      currentOffsetIndicator: 0,
    };
  },
  created() {
    this.limitOfNextDay = moment().subtract(1, 'day').toString();
    this.filters = this.getFilterFromQuery();
    this.selectedDate = this.setDefaultDate();
  },
  watch: {
    selectedDate(value) {
      this.filters.date = value;
      this.load(this.filters);
    },
  },
  computed: {
    isPrevAvailable() {
      return true;
    },
    isNextAvailable() {
      return moment(this.selectedDate).isBefore(this.limitOfNextDay, 'day');
    },
  },
  methods: {
    getPercentiles,
    convertToMbps,
    formatNumber,
    convertFormatDate,
    prevDate() {
      const currentSelected = moment(this.selectedDate);
      this.selectedDate = currentSelected.subtract(1, 'day').toString();
    },
    nextDate() {
      const nextDate = moment(this.selectedDate).add(1, 'day').toString();
      if (moment(nextDate).isAfter(this.limitOfNextDay)) {
        this.selectedDate = this.limitOfNextDay;
      } else {
        this.selectedDate = nextDate;
      }
    },
    changeTab(nextNav) {
      const nextNavIndex = _.findIndex(this.menuList, item => item.key === nextNav.key);

      this.currentOffset = `-${(nextNavIndex * 100)}%`;
      this.currentOffsetIndicator = `${(nextNavIndex * 50)}%`;
      this.state.showingTab = nextNav.key;
    },
    isActivedTab(tabName) {
      return this.state.showingTab === tabName;
    },
    setDefaultDate() {
      if (this.filters.date) {
        return moment(this.filters.date).toString();
      }
      return this.limitOfNextDay;
    },
    load(filters) {
      const selectedDate = convertFormatDate(filters.date);

      this.loadDailyReport(selectedDate);
      this.loadPrimeTimeDailyReport(selectedDate);

      this.filters.date = selectedDate;
      this.changeRoute(this.filters);
    },
    async loadDailyReport(selectedDate) {
      try {
        const daily = await loadDailyReport(selectedDate);
        this.currentDaily.fullDay = this.sortByCount(daily.data);
      } catch (e) {
        this.currentDaily.fullDay = null;
      }
    },
    async loadPrimeTimeDailyReport(selectedDate) {
      try {
        const primeTime2hrs = await loadPrimeTimeDailyReport(selectedDate, '2h');
        this.currentDaily.primeTime2hrs = this.sortByCount(primeTime2hrs.data);
      } catch (e) {
        this.currentDaily.primeTime2hrs = null;
      }
    },
    getFilterFromQuery() {
      return qs.parse(this.$route.query, { ignoreQueryPrefix: true, skipNulls: true });
    },
    changeRoute(filters) {
      const query = qs.stringify(filters, { addQueryPrefix: true, skipNulls: true, encode: false });
      this.$router.replace(query);
    },
    sortByCount(dailys) {
      return _.chain(dailys)
        .orderBy(['count'], ['desc'])
        .slice(0, this.limitOfISP)
        .orderBy(daily => this.getPercentiles(daily.percentiles, this.percentile.min), ['desc'])
        .value();
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import '@/assets/scss/themes/variables.scss';

.filters {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  ::-webkit-scrollbar {
      display: none;
  }

  .actions {
    padding: $spacer;
  }

  .calendar {
    width: calc(100% - 86px);
    overflow: hidden;
  }

  .filters-date {
    margin: 0 $spacer * 0.5;
    cursor: pointer;
    width: 140px;
  }

  .filters-action.disabled {
    .btn-outline-actions {
      opacity: 0.2;
      cursor: not-allowed;
    }
  }

  .btn-outline-actions {
    border: none;
    color: $gray-400;
    height: 100%;
  }
}

.section {
  .section-filter {
    position: relative;
    padding: $spacer 0 $spacer * 0.75 0;

    .nav-mode {
      border: none;

      .nav-item {
        cursor: pointer;
      }

      .nav-link {
        padding: $spacer * 0.5 0;
        border: none;
        &.active {
          font-weight: 400;
          border: none;
        }
      }
    }

    .indicator {
      width: 50%;
      height: 4px;
      position: absolute;
      bottom: 0;
      background: $gray-400;
      transition: 0.3s ease;
    }
  }

  .section-body {
    overflow-x: hidden;

    .content-container {
      transition: all .3s ease-out;
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
    }

    .content {
      width: 100%;
      flex: 1 0 auto;
    }
  }
}


@media (max-width: 767.98px) {
  .section {
    .section-header {
      flex-direction: column;
      padding: $spacer * 0.5 0 $spacer * 0.25 0;
    }
  }

  .filters {
    padding-top: $spacer;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/parts/DailyLeaderBoard.vue